import { LoadIcon } from '@/components/bs-icons/loading'
import { customFetch } from '@/controllers/request'
import { CodeBlock } from '@/modals/formModal/chatMessage/codeBlock'
import { copyText } from '@/utils'
import { ReloadIcon } from '@radix-ui/react-icons'
import type { CollapseProps } from 'antd'
import { Collapse, Image, message, Popover, Spin } from 'antd'
import _, { debounce } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
// import rehypeMathjax from 'rehype-mathjax/svg'
import { sendMessageSave } from '@/controllers/API'
import { uploadToLenovoCloudApi } from '@/controllers/API/assistant'
import { downloadWord } from '@/util/downloadWord'
import { DownloadOutlined } from '@ant-design/icons'
// import { Popover } from 'antd'
import { useAssistantStore } from '@/store/assistantStore'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import { cancelVideoTaskAPI } from './api'
import { useChatStore } from './chatStore'
import MaterialInfoDisplay from './components/MaterialInfoDisplay'
import PopoverCont from './components/PopoverCont'
import SourceLinkList from './components/SourceLinkList'
import LlmIcon from './LlmIcon'
import MessageBs_knowledgeList from './MessageBs_knowledgeList'
import MessageBs_mcp from './MessageBs_mcp'
import './styles.less'
import { ContentType } from './types'
import { getSpecificPluginResultMap, isUrlExpired } from './utils'
import { processMarkdownMessage } from './utils/markdownProcessor'

/**
 * AI助手消息组件
 *
 * 这是聊天系统中AI助手消息的核心组件，负责渲染AI助手的各种类型消息，
 * 包括文本、图片、思考过程、溯源信息、插件调用结果等。
 *
 * 功能特点：
 * - 支持多种消息类型：文本、图片、思考过程
 * - Markdown渲染和代码高亮
 * - 溯源信息展示和交互
 * - 插件调用结果展示
 * - 消息操作：复制、重新生成、编辑
 * - 文件导出功能
 * - 智能链接处理
 * - 响应式布局
 *
 * 使用场景：
 * 1. AI助手回复 - 显示AI助手的文本回复
 * 2. 图片生成 - 展示AI生成的图片
 * 3. 思考过程 - 显示AI的思考过程
 * 4. 溯源展示 - 展示消息的溯源信息
 * 5. 插件调用 - 显示插件调用结果
 * 6. 代码展示 - 高亮显示代码块
 * 7. 文件处理 - 处理文件相关消息
 * 8. 消息操作 - 提供消息的交互操作
 */

/** 头像颜色列表，用于为不同助手生成不同颜色的头像 */
const colorList = [
  '#111111',
  '#FF5733',
  '#3498DB',
  '#27AE60',
  '#E74C3C',
  '#9B59B6',
  '#F1C40F',
  '#34495E',
  '#16A085',
  '#E67E22',
  '#95A5A6'
]

/** 正则表达式，用于匹配 ^[数字]^ 模式的文本，用于处理溯源链接 */
const regex = /\^(\[\d+\])+?\^/g

// URL 验证函数：检查 URL 是否有效
const isValidUrl = (url: string): boolean => {
  if (!url || url.trim() === '') return false

  try {
    // 尝试创建 URL 对象来验证
    // 如果 URL 不完整（比如只有协议），需要先补全
    let testUrl = url.trim()

    // 如果 URL 不包含协议，添加 http:// 进行测试
    if (!testUrl.match(/^https?:\/\//i)) {
      testUrl = 'http://' + testUrl
    }

    const urlObj = new URL(testUrl)

    // 检查是否有有效的域名
    if (!urlObj.hostname || urlObj.hostname.length === 0) {
      return false
    }

    // 检查域名格式（至少包含一个点，或者 localhost）
    if (!urlObj.hostname.includes('.') && urlObj.hostname !== 'localhost') {
      return false
    }

    return true
  } catch (error) {
    // URL 解析失败，说明格式无效
    return false
  }
}

// 处理包含www链接的混合文本
const processWwwLinksInText = (linkText: string, props: any) => {
  // 查找所有www链接，使用更严格的正则表达式验证域名格式
  const wwwMatches = linkText.match(/www\.[a-zA-Z0-9]([a-zA-Z0-9\-\.]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+/g)

  if (!wwwMatches || wwwMatches.length === 0) {
    return null
  }

  // 将文本分割并渲染
  const parts = []
  let lastIndex = 0

  wwwMatches.forEach((wwwLink, index) => {
    const linkIndex = linkText.indexOf(wwwLink, lastIndex)

    // 添加链接前的文本
    if (linkIndex > lastIndex) {
      parts.push(linkText.substring(lastIndex, linkIndex))
    }

    // 添加链接
    parts.push(
      <a
        key={index}
        href={`https://${wwwLink}`}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative inline-block cursor-pointer text-blue-500 no-underline"
        {...props}
      >
        {wwwLink}
      </a>
    )

    lastIndex = linkIndex + wwwLink.length
  })

  // 添加最后的文本
  if (lastIndex < linkText.length) {
    parts.push(linkText.substring(lastIndex))
  }

  return <>{parts}</>
}

/**
 * 加载文本组件，带有点号循环动画
 */
const LoadingDotsText = () => {
  const [dots, setDots] = useState('...')

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev === '.') return '..'
        if (prev === '..') return '...'
        return '.'
      })
    }, 500) // 每 500ms 切换一次

    return () => clearInterval(interval)
  }, [])

  return <span className="block w-[215px]">正在加速生成中，请稍等片刻{dots}</span>
}

export default function MessageBs({
  data,
  onUnlike = () => {},
  onSource,
  backGrou,
  citations,
  hitPlugin,
  chatting,
  nextDataInfo,
  lastUserInfo,
  showMcpOutput = false,
  conversationId = '',
  conversation = '',
  logo = ''
}: {
  data: ContentType | any
  onUnlike?: any
  onSource?: any
  backGrou: string
  citations?: any
  hitPlugin?: any
  chatting: string
  nextDataInfo?: any
  lastUserInfo?: any
  showMcpOutput?: boolean
  conversationId?: string
  conversation?: string
  logo?: string
}) {
  // 判断是否为图片类型消息
  const isImg = data?.type === 'image_url'

  // 从聊天状态管理中获取相关状态和方法
  const {
    chatId,
    lastIsEndSuccess,
    tool_ids,
    tool_enable,
    createSendMsg,
    updateCurrentMessage,
    hisType,
    setIsFix,
    addMessagePair,
    audioPlayingId,
    setAudioPlayingId,
    updateVideoTaskStatus,
    stopVideoTaskPolling
  } = useChatStore()

  // 从助手状态管理中获取助手信息
  const { assistantState } = useAssistantStore()

  // 插件状态映射，用于跟踪特定插件的启用状态
  const pluginStatusMap = useRef({
    includedAudioPlugin: false,
    includedLenovoCloudPlugin: false
  })

  // 检查上一条用户消息是否包含文件
  const preIncludedFile = useMemo(() => {
    return lastUserInfo?.content?.some((item: any) => item?.file_url?.name)
  }, [lastUserInfo])

  // 提取上一条用户消息中的文件名（去除扩展名）
  const preFileName = useMemo(() => {
    const fileInfo = lastUserInfo?.content?.find((item: any) => item?.file_url?.name)
    if (!fileInfo?.file_url?.name) return ''
    const name = fileInfo.file_url.name
    const lastDotIndex = name.lastIndexOf('.')
    return lastDotIndex !== -1 ? name.substring(0, lastDotIndex) : name
  }, [lastUserInfo])

  /**
   * 数据变化时更新组件状态
   * 重置发送保存状态和间隔隐藏状态，更新插件状态映射
   */
  useEffect(() => {
    setSendSave(true)
    setIntervalHid(true)
    pluginStatusMap.current = getSpecificPluginResultMap(tool_ids, tool_enable)
  }, [data])
  const citationsIsKnowledge = citations && citations[0]?.type === 'knowledge'
  let msg = data.text

  /**
   * 处理文本消息：转换溯源链接、格式化 URL、规范化链接格式
   */
  if (data.type === 'text' && msg) {
    msg = processMarkdownMessage(msg, {
      citationsIsKnowledge,
      citations,
      internetCitations: data?.internetCitations,
      enableDebug: false // 设置为 true 可以启用调试日志
    })
  }

  function getTraceableBadges(msgText) {
    const regex = /\^(\[\d+\])+?\^/g
    const matches = msgText.match(regex) || []

    const badges = []
    matches.forEach(match => {
      const innerContent = match.slice(1, -1) // 去掉 ^ 符号
      const numbers = innerContent.split('][').map(numStr => {
        return numStr.replace(/^\[|\]$/g, '') // 去掉方括号，得到数字
      })
      badges.push(...numbers)
    })

    return _.uniq(badges.map(n => parseInt(n)))
  }

  // 根据索引查找数据
  const findCitationByIndex = (index, citations) => {
    if (!citations || index < 1 || index > citations.length) {
      return null // 如果索引无效，返回 null
    }
    return citations[index - 1] // 索引从 1 开始，数组从 0 开始
  }

  /**
   * 处理消息重发功能
   * 根据上一条用户消息重新发送请求
   * @param send - 是否发送消息（true）还是编辑（false）
   */
  const handleResend = send => {
    const nextData = nextDataInfo.nextData
    if (!nextData) return

    // 从前3个content中提取文本/图片/文件，兼容历史消息结构
    const contentList = (nextData.content || []).slice(0, 3)
    const validItem = contentList.find(item => item && item.type)
    const validTextItem = contentList.find(item => item && item.type === 'text')
    const validImageItem = contentList.find(item => item && item.type === 'image_url')
    const fileItems = contentList
      .filter(item => item && item.type === 'file_url')
      .map(item => ({ fileUrl: item.file_url?.url || '', fileName: item.file_url?.name || '' }))
      .filter(file => file.fileUrl || file.fileName)

    if (!validItem) return

    // 提取 material_info（通义万相模型的数据）
    let materialInfo: Array<{ type: string; url: string; name: string }> = []
    const materialItem = (nextData.content || []).find(item => item && item.type === 'material' && item.material_info)
    if (materialItem && materialItem.material_info) {
      materialInfo = materialItem.material_info
    }

    // 创建自定义事件，通知父组件重新发送消息
    const myEvent = new CustomEvent('userResendMsgEvent', {
      detail: {
        send,
        message: validTextItem?.text || '',
        imageUrl: validImageItem?.image_url?.url || '',
        originalUrl: validImageItem?.image_url?.originalUrl || validImageItem?.image_url?.url || '',
        imageName: validImageItem?.image_url?.name || '',
        // 兼容旧逻辑：保留单文件字段
        fileUrl: fileItems[0]?.fileUrl || '',
        fileName: fileItems[0]?.fileName || '',
        // 新增多文件字段，供输入框完整回显与重发
        files: fileItems,
        materialInfo: materialInfo // 添加 material_info
      }
    })
    document.dispatchEvent(myEvent)
  }

  // 头像颜色，使用第一个颜色
  const avatarColor = colorList[0]
  /* popover显示状态 */
  // const [PopoverIndexHid, setPopoverIndexHid] = useState(null)
  /* 匹配单独msg来渲染html */
  const markDownList = [
    {
      msg: '__send_message_success__',
      html: '<div style="display:flex;align-items: center;"><svg class="mr-[4px]" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.5 7.5L8 10L15 3" stroke="#38D0AF" stroke-linecap="round" stroke-linejoin="round"/><path d="M8 1C9.54097 1 10.9648 1.49903 12.1211 2.34277L11.4043 3.05957C10.437 2.39175 9.26439 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C11.3137 14 14 11.3137 14 8C14 7.8495 13.9923 7.70045 13.9814 7.55273L14.8701 6.66406C14.9537 7.09666 15 7.54298 15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1Z" fill="#38D0AF"/></svg><span>消息发送成功</span></div>'
    }
  ]

  /**
   * 获取特殊消息的HTML内容
   * @param text - 消息文本
   * @returns 对应的HTML字符串或undefined
   */
  function getMarkDownHtml(text: string) {
    const item = markDownList.find(item => item.msg === text)
    return item?.html
  }

  // 检查当前消息是否匹配特殊HTML渲染
  const matchedHtml = getMarkDownHtml(data?.text)
  /**
   * Markdown渲染组件
   * 使用ReactMarkdown渲染消息内容，支持代码高亮、链接处理等
   */
  const mkdown = useMemo(
    () => (
      <>
        {/* 特殊消息通知或匹配的HTML内容 */}
        {data?.hitPlugin?.msgType === 'notify' || matchedHtml ? (
          <div
            className="bs-mkdown inline-block max-w-full whitespace-break-spaces break-all text-sm text-text-answer"
            dangerouslySetInnerHTML={{ __html: matchedHtml || msg }}
          />
        ) : data.text ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]} // 支持GitHub风格Markdown和数学公式
            // rehypePlugins={[rehypeMathjax]} // 数学公式渲染（已注释）
            linkTarget="_blank" // 链接在新窗口打开
            className="bs-mkdown inline-block max-w-full whitespace-break-spaces break-all text-sm text-text-answer"
            components={{
              /**
               * 自定义代码块渲染
               * 支持代码高亮和特殊字符处理
               */
              code: ({ node, inline, className, children, ...props }) => {
                if (children.length) {
                  // 处理特殊光标字符
                  if (children[0] === '▍') {
                    return <span className="form-modal-markdown-span"> ▍ </span>
                  }

                  children[0] = (children[0] as string).replace('`▍`', '▍')
                }

                // 提取编程语言
                const match = /language-(\w+)/.exec(className || '')

                return !inline ? (
                  // 代码块：使用CodeBlock组件进行高亮
                  <CodeBlock
                    key={Math.random()}
                    language={(match && match[1]) || ''}
                    value={String(children).replace(/\n$/, '')}
                    {...props}
                  />
                ) : (
                  // 行内代码：使用普通code标签
                  <code className={className} {...props}>
                    {' '}
                    {children}{' '}
                  </code>
                )
              },
              /**
               * 自定义链接渲染
               * 处理HTTP链接、图片链接和特殊文本分离
               */
              a: ({ href, title, children, ...props }) => {
                const linkText = Array.isArray(children)
                  ? (children[0] as string)
                  : typeof children === 'string'
                  ? children
                  : String(children)

                // 去除 href 和 linkText 中的空格
                const trimmedHref = href?.trim()
                const trimmedLinkText = linkText?.trim()

                // 处理 www. 域名的特殊情况：如果 linkText 是 www.xxx，href 是 https://www.xxx，应该显示 www.xxx
                if (
                  trimmedHref &&
                  trimmedLinkText &&
                  trimmedLinkText.startsWith('www.') &&
                  trimmedHref.includes(trimmedLinkText)
                ) {
                  // 确保 href 是完整的 URL
                  const finalHref =
                    trimmedHref.startsWith('http://') || trimmedHref.startsWith('https://')
                      ? trimmedHref
                      : `https://${trimmedHref}`
                  return (
                    <a
                      href={finalHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative inline-block cursor-pointer text-blue-500 no-underline"
                      {...props}
                    >
                      {trimmedLinkText}
                    </a>
                  )
                }

                // 如果是自动链接（href 和 linkText 相同），直接渲染
                if (trimmedHref && trimmedLinkText === trimmedHref) {
                  return (
                    <a
                      href={trimmedHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative inline-block cursor-pointer text-blue-500 no-underline"
                      {...props}
                    >
                      {trimmedLinkText}
                    </a>
                  )
                }

                // 检查 linkText 是否包含 URL 和额外的文字（ReactMarkdown 可能错误地将文字包含在链接中）
                // 例如：linkText 可能是 "https://www.lenovo.com/，覆盖国际业务信息"
                if (
                  trimmedHref &&
                  trimmedLinkText &&
                  trimmedLinkText.startsWith(trimmedHref) &&
                  trimmedLinkText.length > trimmedHref.length
                ) {
                  // 分离 URL 和后面的文字
                  const extraText = trimmedLinkText.substring(trimmedHref.length)
                  return (
                    <>
                      <a
                        href={trimmedHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative inline-block cursor-pointer text-blue-500 no-underline"
                        {...props}
                      >
                        {trimmedHref}
                      </a>
                      {extraText}
                    </>
                  )
                }

                // 检查链接文本是否异常长
                const isTooLong = linkText?.length > 100 // 链接文本超过100个字符认为异常
                const hasChinese = /[\u4e00-\u9fa5]/.test(linkText)

                // 如果链接文本包含www链接和中文，尝试提取所有www链接
                // 但要注意：如果文本包含完整的URL（https://或http://开头），不应该用这个函数处理
                // 因为这会错误地只匹配域名部分，导致URL路径丢失
                if (hasChinese && linkText?.includes('www.') && !linkText?.match(/^https?:\/\//)) {
                  const processedLinks = processWwwLinksInText(linkText, props)
                  if (processedLinks) {
                    return processedLinks
                  }
                }

                // 如果链接文本过长，不渲染为链接（但允许中文链接文本正常渲染）
                if (isTooLong) {
                  return <span {...props}>{children}</span>
                }

                const isHttpLink = href?.startsWith('http://') || href?.startsWith('https://')
                const isWwwLink = href?.startsWith('www.') || linkText?.startsWith('www.')

                // 判断是否子节点中包含 img 标签
                const containsImg =
                  Array.isArray(children) &&
                  children.some((child: any) => typeof child === 'object' && child?.type === 'img')

                if (containsImg && !isHttpLink) {
                  // 找出 img 子节点，显示它的 src
                  const imgNode = children.find(
                    (child: any) => typeof child === 'object' && child?.type === 'img'
                  ) as any
                  const src = imgNode?.props?.src || href || '链接错误'
                  return <span className="text-gray-400">{src}</span>
                }

                if (!isHttpLink && !isWwwLink) {
                  // 其他不是 http(s) 或 www 的链接，不渲染成链接
                  return <span {...props}>{children}</span>
                }
                // 提取引用链接的索引（如果链接文本是纯数字或 [数字] 格式）
                const childrenText = Array.isArray(children)
                  ? (children[0] as string)
                  : typeof children === 'string'
                  ? children
                  : String(children)
                const indexMatch = childrenText?.match(/^\[?(\d+)\]?$/)
                const index = indexMatch ? Number(indexMatch[1]) : null

                // const isVisible = PopoverIndexHid === index
                const [isVisible, setIsVisible] = useState(false)
                /* 溯源信息 */
                const [chunkInfo, setChunkInfo] = useState({})

                // 修复 Markdown 把链接和后面文本合并进一个 <a> 的问题
                // 比如 children 是 ['https://abcd.com 无法提供信息。'] = 分离出来
                const fullText = childrenText
                const match = fullText?.match(
                  /^(https?:\/\/[^\s\u4e00-\u9fa5<>()（）【】「」""''、。，！？；：\[\]{}"'`]+|www\.[a-zA-Z0-9]([a-zA-Z0-9\-\.]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+)(.*)$/
                )

                if (match) {
                  const [, urlText, suffixText] = match
                  // 去除 URL 末尾可能的空格
                  const trimmedUrlText = urlText.trim()
                  // 为www链接添加https://前缀
                  const finalHref = trimmedUrlText.startsWith('www.') ? `https://${trimmedUrlText}` : trimmedUrlText

                  // 验证 URL 是否有效
                  if (!isValidUrl(finalHref)) {
                    // URL 无效，显示为普通文本，不渲染为链接，也不截断
                    return <span {...props}>{fullText}</span>
                  }

                  return (
                    <>
                      <a
                        href={finalHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative inline-block cursor-pointer text-blue-500 no-underline"
                        {...props}
                      >
                        {trimmedUrlText}
                      </a>
                      {suffixText}
                    </>
                  )
                }

                // 对于普通链接，使用去除空格后的 href 和 linkText
                const finalHref = trimmedHref || href
                const finalLinkText = trimmedLinkText || linkText

                // 验证 URL 是否有效（非引用链接才验证）
                if (finalHref && !citationsIsKnowledge && index !== null && index > 0) {
                  // 这是引用链接，不验证
                } else if (finalHref && !isValidUrl(finalHref)) {
                  // URL 无效，显示为普通文本，不渲染为链接，也不截断
                  return <span {...props}>{children}</span>
                }

                return (
                  <>
                    {citationsIsKnowledge && index !== null && index > 0 ? (
                      <Popover
                        // open={true}
                        open={isVisible}
                        content={<PopoverCont info={chunkInfo} onClose={() => setIsVisible(false)} />}
                        trigger="click"
                        placement="bottom"
                        overlayClassName="custom-popover-style"
                        onOpenChange={visible => {
                          // setPopoverIndexHid(visible ? index : null)
                          setIsVisible(visible)
                        }}
                      >
                        <span
                          className="group relative inline-block cursor-pointer text-blue-500 no-underline"
                          onClick={() => {
                            const clickedData = findCitationByIndex(index, citations)
                            if (clickedData && clickedData.detail && clickedData.detail[0]) {
                              setChunkInfo(clickedData)
                            }
                          }}
                        >
                          {/* 知识库溯源链接显示为 [数字] 格式 */}[{index}]
                        </span>
                      </Popover>
                    ) : (
                      <a
                        href={finalHref?.startsWith('www.') ? `https://${finalHref}` : finalHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative inline-block cursor-pointer text-blue-500 no-underline"
                        {...props}
                      >
                        {/* 如果是数字格式的引用链接，显示为 [数字] 格式，否则显示原始内容 */}
                        {index !== null && index > 0 ? `[${index}]` : children || finalLinkText}
                      </a>
                    )}
                  </>

                  /* 溯源-展示 */
                )
              },

              // 添加对 div 标签的自定义渲染逻辑
              div: ({ node, children, ...props }) => {
                return <div {...props}>{children}</div>
              },
              /**
               * 自定义 p 标签渲染
               * 确保链接和引用链接在同一行显示
               */
              p: ({ node, children, ...props }) => {
                return (
                  <p {...props} className="markdown-paragraph">
                    <style>{`
                      .markdown-paragraph a,
                      .markdown-paragraph span {
                        display: inline !important;
                      }
                      .markdown-paragraph a + span {
                        margin-left: 4px;
                      }
                    `}</style>
                    {children}
                  </p>
                )
              },
              /**
               * 自定义 br 标签渲染
               * 如果 br 在链接和引用链接之间，则隐藏它（通过 CSS 处理）
               */
              // br: ({ node, ...props }: any) => {
              //   return <br {...props} />
              // },
              /**
               * 自定义span标签渲染
               */
              span: ({ node, children, ...props }) => {
                return <span {...props}>{children}</span>
              }
            }}
          >
            {msg?.toString()}
          </ReactMarkdown>
        ) : null}

        {/* 图片消息渲染 */}
        {isImg && (
          <Image
            preview={{
              destroyOnClose: true, // 预览关闭时销毁
              toolbarRender: () => null, // 隐藏工具栏
              mask: <></> // 无遮罩
            }}
            className="cursor-zoom-in rounded-[8px]"
            width={240}
            height={240}
            src={data.image_url?.url}
          />
        )}
      </>
    ),
    [data.type, data.image_url?.url, data.text]
  )

  // 消息内容引用，用于复制功能
  const messageRef = useRef<HTMLDivElement>(null)

  /**
   * 复制消息内容
   */
  const handleCopyMessage = () => {
    copyText(messageRef.current)
  }

  /**
   * 联网溯源信息列表组件
   * 以折叠面板形式展示联网搜索的参考资料
   */
  const internetCitationsList = () => {
    return (
      <Collapse
        className="mb-[8px]"
        size="small"
        items={[
          {
            key: '1',
            label: `找到了${data.internetCitations.length}篇资料作为参考`,
            children: (
              <div className="flexCol">
                {data?.internetCitations?.length > 0 &&
                  data.internetCitations.map((item, index) => {
                    return (
                      <div key={index} className="mt-[4px]">
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block cursor-pointer text-blue-500 no-underline"
                        >
                          {index + 1}. {item.title}
                        </a>
                      </div>
                    )
                  })}
              </div>
            )
          }
        ]}
      />
    )
  }

  // 思考过程折叠面板的激活状态
  const [activeKey, setActiveKey] = useState<string[]>(['1'])

  /**
   * 渲染思考过程标题文本
   * @returns 思考状态的显示文本
   */
  const renderText = () => {
    if (data?.thinkInfo?.type === 'stop' && !data?.text) return data?.thinkInfo?.text
    if (!data?.text) return '思考中...'
    const timeText = data.thinkTime ? `（用时${data.thinkTime}秒）` : ''
    return `已深度思考${timeText}`
  }

  /**
   * 思考过程折叠面板的标签组件
   * 包含思考状态文本和展开/收起图标
   */
  const ThinkItemLabel = (
    <div className="bs-think-item-label">
      <div>{renderText()}</div>
      <div>
        {activeKey.length ? (
          // 展开状态：向下箭头
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 8L10 12L14 8" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          // 收起状态：向右箭头
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 14L12 10L8 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
    </div>
  )

  /**
   * 思考过程折叠面板配置
   */
  const ThinkItem: CollapseProps['items'] = [
    {
      key: '1',
      label: ThinkItemLabel,
      children: (
        <div>
          <div className="bs-think-collapse-left"></div>
          <div className="bs-think-collapse-content inline-block max-w-full whitespace-break-spaces break-all text-text-answer">
            {data?.think}
          </div>
        </div>
      ),
      showArrow: false // 隐藏默认箭头，使用自定义图标
    }
  ]

  // Word导出加载状态
  const [exportWordIsLoading, setExportWordIsLoading] = useState(false)

  /**
   * 导出消息内容为Word文档
   */
  const exportWord = async () => {
    try {
      setExportWordIsLoading(true)
      await downloadWord({
        name: preFileName,
        content: msg?.toString()
      })
      message.success('总结内容下载至本地成功')
    } catch (error) {
      console.error('总结内容下载至本地失败:', error)
    } finally {
      setExportWordIsLoading(false)
    }
  }

  // 联想云上传加载状态
  const [uploadToLenovoIsLoading, setUploadToLenovoIsLoading] = useState(false)

  /**
   * 上传消息内容到联想云空间
   */
  const uploadToLenovoCloud = async () => {
    try {
      setUploadToLenovoIsLoading(true)
      createSendMsg('text', '总结内容存入联想云空间', '', '', '', '', true)
      updateCurrentMessage(chatId, '', 1)
      await uploadToLenovoCloudApi({
        name: preFileName,
        content: msg?.toString(),
        sso_key: 'test'
      })
      message.success('总结内容存入联想云空间成功')
      updateCurrentMessage(chatId, `总结内容存入联想云空间成功`, 1)
    } catch (error) {
      console.error('总结内容存入联想云空间失败:', error)
      updateCurrentMessage(chatId, `总结内容存入联想云空间失败`, 1)
    } finally {
      setUploadToLenovoIsLoading(false)
    }
  }

  /**
   * 下载图片到本地
   * @param imgSrc - 图片源地址
   * @param filename - 下载的文件名
   */
  const downloadImage = (imgSrc, filename) => {
    const image = new window.Image()
    image.crossOrigin = 'anonymous'
    image.src = imgSrc
    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = image.width
      canvas.height = image.height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(image, 0, 0)

      const link = document.createElement('a')
      link.download = filename
      link.href = canvas.toDataURL('image/jpeg')
      link.click()
    }
  }

  const [sendSave, setSendSave] = useState(true)
  const [IntervalHid, setIntervalHid] = useState(true)
  // 检查是否需要显示修改按钮
  const shouldShowEditButton = useMemo(() => {
    if (!hitPlugin) return false
    try {
      // 检查 hitPlugin 和 fix_num
      const fixNum = hitPlugin?.fix_num

      // 检查所有条件
      return (
        sendSave && // 发送保存
        IntervalHid && // 发送中间消失判断
        hitPlugin?.id === '10883' && // hitPlugin id 为 10883
        fixNum !== undefined && // fix_num 存在
        tool_ids.length > 0 && // tool_ids 不为空
        tool_ids.includes(10883) && // tool_ids 包含 10883
        citations[0]?.type === 'plugin' && // citations 类型为 plugin
        citations[0]?.title === '消息通知' // citations title 为 消息通知
        // citations[0]?.title === 'send_message' // citations title 为 send_message
        /* 0716 tag 上线注释 */
        // !hisType // 不是历史消息
      )
    } catch (error) {
      console.error('Error checking conditions:', error)
      return false
    }
  }, [sendSave, hitPlugin, tool_ids, citations, hisType])
  /* send消息通知按钮 - 修改 确定 */
  const getSuccessMessageHtml = () => {
    setSendSave(false)
    setIntervalHid(false)

    // 添加成功消息到消息对
    addMessagePair(
      '确认',
      `<div style="display:flex;align-items: center;"><svg class="mr-[4px]" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.5 7.5L8 10L15 3" stroke="#38D0AF" stroke-linecap="round" stroke-linejoin="round"/><path d="M8 1C9.54097 1 10.9648 1.49903 12.1211 2.34277L11.4043 3.05957C10.437 2.39175 9.26439 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C11.3137 14 14 11.3137 14 8C14 7.8495 13.9923 7.70045 13.9814 7.55273L14.8701 6.66406C14.9537 7.09666 15 7.54298 15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1Z" fill="#38D0AF"/></svg><span>消息发送成功</span></div>`,
      {
        id: '10883',
        name: '发送消息',
        msgType: 'notify',
        content: '发送消息'
      }
    )
  }

  /**
   * 防抖的确认点击处理函数
   * 处理消息确认操作，包括保存到服务器
   */
  const debouncedConfirmClick = useMemo(
    () =>
      debounce(() => {
        /* 应用编排-不发送成功-不调接口 */
        if (!conversation) {
          getSuccessMessageHtml()
          return
        }
        const data = {
          conversation_id: conversationId,
          content: nextDataInfo?.data[0]?.text || '',
          assistant_id: assistantState?.id || ''
        }
        // 发送保存成功
        sendMessageSave(data).then(res => {
          getSuccessMessageHtml()
        })
      }, 1000),
    [conversationId, nextDataInfo, sendMessageSave, conversation]
  )
  const sendBtn = () => {
    return (
      <div className="flexRowStart ml-[34px] mt-[12px]">
        {/* 修改按钮：限制编辑次数为2次 */}
        {hitPlugin?.fix_num < 2 && (
          <div
            className="cursonCase sendEditBtn flexRow mr-[12px] w-[96px] bg-[#ffffff] text-[#131921]"
            style={{
              border: '1px solid #6468f7',
              transform: 'translateZ(0)', // 强制GPU加速
              backfaceVisibility: 'hidden', // 避免背面渲染问题
              willChange: 'transform' // 提示浏览器该元素会发生变化
            }}
            onClick={() => {
              setIsFix(true)
              setIntervalHid(false)
              handleResend(false)
            }}
          >
            修改
          </div>
        )}

        {/* 确认按钮 */}
        <div
          className="cursonCase sendEditBtn flexRow w-[96px] text-[#FFFFFF]"
          style={{
            background: 'linear-gradient(to right, #3b74fb, #6468f7, #9659f2)',
            transform: 'translateZ(0)', // 强制GPU加速
            backfaceVisibility: 'hidden', // 避免背面渲染问题
            willChange: 'transform' // 提示浏览器该元素会发生变化
          }}
          onClick={debouncedConfirmClick}
        >
          确认
        </div>
      </div>
    )
  }

  return (
    <div className="flex w-full py-1">
      <div className="w-fit max-w-[90%]">
        {/* 助手头像和名称 */}
        <div className="flex gap-[12px]">
          {logo ? (
            // rounded-full
            <img className="flex h-6 w-6 min-w-6 items-center justify-center rounded-[12px]" src={logo} alt="" />
          ) : (
            // 使用默认AI图标
            <LlmIcon avatarColor={avatarColor} />
          )}

          <span className="chat-role-name">智能助手</span>
        </div>
        {/* 消息内容容器 */}
        <div
          className={`chat-custom-content ml-[34px] min-h-8 rounded-[12px] bg-[#F5F7FB] p-[16px] dark:bg-[#313336] ${backGrou} ${
            isImg && !msg?.toString() ? 'chat-img-content rounded-[8px] p-[0px]' : `${isImg && 'min-w-fit pb-[0px]'}`
          }`}
        >
          <div className={msg?.toString() ? 'flex gap-2' : 'flexRow gap-2'}>
            {msg || data?.think || data?.image_url?.url || data?.generate_video ? (
              <div ref={messageRef} className={`max-w-[100%] text-sm ${isImg ? 'flexCol flex' : ''}`}>
                {/* 联网展示信息 */}
                {/* {data?.internetCitations?.length > 0 && internetCitationsList()} */}
                {data?.internetCitations?.length > 0 && <SourceLinkList data={data.internetCitations} />}
                {data?.think && (
                  <Collapse
                    className="bs-think-collapse"
                    defaultActiveKey={['1']}
                    ghost
                    items={ThinkItem}
                    onChange={(key: any) => {
                      setActiveKey(key)
                    }}
                  />
                )}
                {/* MCP输出展示 */}
                {citations[0]?.type === 'mcp' && (
                  <MessageBs_mcp list={citations} showMcpOutput={showMcpOutput}></MessageBs_mcp>
                )}
                {/* 视频展示 */}
                {(data?.generate_video?.task_status || data?.generate_video?.is_valid == 1) && (
                  <>
                    {/* 如果任务状态是 PENDING 或 RUNNING，显示 loading */}
                    {(data.generate_video.task_status === 'PENDING' ||
                      data.generate_video.task_status === 'RUNNING') && <LoadingDotsText />}
                    {/* 如果任务状态是 CANCELED，且没有其他内容，显示取消提示 */}
                    {data.generate_video.task_status === 'CANCELED' && <span>生成任务已取消</span>}
                    {/* 如果任务状态是 FAILED，显示失败提示 */}
                    {data.generate_video.task_status === 'FAILED' && <span>生成任务失败，出现异常，请稍后重试</span>}
                    {/* 如果任务完成且有视频URL，显示视频 */}
                    {data.generate_video.url &&
                      (data.generate_video.task_status === 'SUCCEEDED' || data.generate_video.is_valid == 1) && (
                        <MaterialInfoDisplay
                          materialInfo={[
                            {
                              type: 'video',
                              name: data?.generate_video?.name || '视频',
                              url: data?.generate_video?.url,
                              is_valid: data?.generate_video?.is_valid,
                              thumbnail_url: data?.generate_video?.thumbnail_url
                            }
                          ]}
                          hasText={!!msg}
                          audioPlayingId={audioPlayingId}
                          setAudioPlayingId={setAudioPlayingId}
                        />
                      )}
                  </>
                )}
                {/* 主要消息内容 */}
                {/* {data.text && mkdown} */}
                {mkdown}
                {/* Word导出按钮 - 不包含联想云插件时显示 */}
                {lastIsEndSuccess && preIncludedFile && !pluginStatusMap.current.includedLenovoCloudPlugin && (
                  <Spin spinning={exportWordIsLoading}>
                    <div
                      className="chat_save_and_export_btn chat_save_to_word_btn2 bg-[#D1DAF9]"
                      onClick={() => {
                        exportWord()
                      }}
                    >
                      <div className="word_btn_save_text">总结内容下载至本地</div>
                    </div>
                  </Spin>
                )}
                {/* Word导出按钮 - 包含联想云插件时也显示 */}
                {lastIsEndSuccess && preIncludedFile && pluginStatusMap.current.includedLenovoCloudPlugin && (
                  <Spin spinning={exportWordIsLoading}>
                    <div
                      className="chat_save_and_export_btn chat_save_to_word_btn2 bg-[#D1DAF9]"
                      onClick={() => {
                        exportWord()
                      }}
                    >
                      <div className="word_btn_save_text">总结内容下载至本地</div>
                    </div>
                  </Spin>
                )}
                {/* 联想云上传按钮 - 包含联想云插件时显示 */}
                {lastIsEndSuccess && preIncludedFile && pluginStatusMap.current.includedLenovoCloudPlugin && (
                  <Spin spinning={uploadToLenovoIsLoading}>
                    <div
                      className="chat_save_and_export_btn chat_save_to_cloud_btn"
                      onClick={() => {
                        uploadToLenovoCloud()
                      }}
                    >
                      <div className="word_btn_save_text">总结内容存入联想云空间</div>
                    </div>
                  </Spin>
                )}
                {citations[0]?.type === 'mcp' && (
                  <div className="flex w-full justify-end" style={{ position: 'relative', top: '16px' }}>
                    <div className="text-xs text-gray-500">MCP Server</div>
                  </div>
                )}
                {/* 知识库和插件引用列表（图片类型不展示） */}
                {citations?.length > 0 &&
                  citations[0]?.type != 'internet' &&
                  citations[0]?.type != 'mcp' &&
                  data?.type !== 'image_url' &&
                  !(nextDataInfo?.index === nextDataInfo?.MaxLength - 1 && chatting !== 'again') &&
                  (citations?.length === 1 && citations[0]?.type !== 'plugin' && !citations[0].knowledge_name ? (
                    <></>
                  ) : (
                    <MessageBs_knowledgeList
                      title={`${citations[0].type === 'plugin' ? '' : `引用知识库（${citations.length}）`}`}
                      list={citations}
                      badges={getTraceableBadges(data.text)}
                    ></MessageBs_knowledgeList>
                  ))}
                {/* @user */}
                {/* {data.receiver && <p className="text-sm text-blue-500">@ {data.receiver.user_name}</p>} */}
                {/* 光标 */}
                {/* {data.message.toString() && !data.end && <div className="animate-cursor absolute w-2 h-5 ml-1 bg-gray-600" style={{ left: cursor.x, top: cursor.y }}></div>} */}
                {/* <br style={{ userSelect: 'none' }} /> */}
                {/* 停止生成标识 */}
                {data?.chatStopTag && <div className="chatStopSize mt-[12px]">已停止生成</div>}
              </div>
            ) : (
              // 加载状态显示
              <LoadIcon className="text-gray-400" />
            )}
          </div>
        </div>

        {/* 操作按钮区域 */}
        {isImg ? (
          // 图片下载按钮
          <div
            className="flexRowStart chatMessageBotSize cursonCase ml-[34px] mt-[6px] cursor-pointer"
            onClick={() => {
              downloadImage(data.image_url?.url, data.image_url?.filename)
            }}
          >
            <DownloadOutlined className="mr-[4px] hover:text-gray-500" />
          </div>
        ) : (
          //     图片类型不用点击重新生成
          //     chatting = again 回答结束 = stop 回答中 = '' 接受消息中
          //     nextDataInfo.nextData 判断上一条 != 第一条
          //     nextDataInfo.index MaxLength 最后一条显示icon功能
          //
          // 文本消息操作按钮
          <div className="flexRowStart">
            {/* 重新生成按钮 - 聊天状态为again时显示，且没有视频任务状态 */}
            {chatting === 'again' &&
              !nextDataInfo?.data[0]?.generate_video &&
              nextDataInfo.nextData &&
              nextDataInfo.index !== 0 &&
              !data.generate_video?.task_status &&
              nextDataInfo.index === nextDataInfo.MaxLength - 1 &&
              (shouldShowEditButton
                ? sendBtn() // 显示修改/确认按钮
                : nextDataInfo?.data &&
                  // 判断不是消息通知
                  nextDataInfo?.data[0]?.hitPlugin?.msgType !== 'notify' &&
                  // 判断不是文件格式（文件不能重新生成）
                  !preIncludedFile && (
                    <div
                      className="flexRowStart chatMessageBotSize cursonCase ml-[34px] mt-[6px]"
                      onClick={() => handleResend(true)}
                    >
                      <ReloadIcon className="mr-[4px] hover:text-gray-500" />
                      重新生成
                    </div>
                  ))}

            {/* 停止生成按钮 - 聊天状态为stop时显示，且没有视频任务状态 */}
            {chatting === 'stop' &&
              !data.generate_video?.task_status &&
              nextDataInfo.nextData &&
              nextDataInfo.index !== 0 &&
              nextDataInfo.index === nextDataInfo.MaxLength - 1 && (
                <div
                  className="flexRowStart chatMessageBotSize cursonCase ml-[34px] mt-[6px]"
                  onClick={() => handleResend('stop')}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    className="mr-[4px]"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_387_17616)">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M1.5 7C1.5 3.96243 3.96243 1.5 7 1.5C10.0376 1.5 12.5 3.96243 12.5 7C12.5 10.0376 10.0376 12.5 7 12.5C3.96243 12.5 1.5 10.0376 1.5 7ZM7 0.5C3.41015 0.5 0.5 3.41015 0.5 7C0.5 10.5899 3.41015 13.5 7 13.5C10.5899 13.5 13.5 10.5899 13.5 7C13.5 3.41015 10.5899 0.5 7 0.5ZM6 5C5.44772 5 5 5.44772 5 6V8C5 8.55229 5.44772 9 6 9H8C8.55229 9 9 8.55229 9 8V6C9 5.44772 8.55229 5 8 5H6Z"
                        fill="#7587A5"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_387_17616">
                        <rect width="14" height="14" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  停止生成
                </div>
              )}
            {/* 万象模型icon - 有视频任务状态时显示 */}
            {nextDataInfo.nextData &&
              nextDataInfo.index !== 0 &&
              nextDataInfo.index === nextDataInfo.MaxLength - 1 &&
              (data.generate_video?.task_status || data.generate_video?.is_valid == 1) &&
              (data.generate_video?.task_status === 'SUCCEEDED' ||
              data.generate_video?.task_status === 'CANCELED' ||
              data.generate_video?.task_status === 'FAILED' ||
              data.generate_video?.is_valid == 1 ? (
                <div className="flexRowStart">
                  <div
                    className="flexRowStart chatMessageBotSize cursonCase ml-[34px] mt-[6px]"
                    onClick={() => {
                      // 如果是重发（send === true），先停止当前的视频任务轮询
                      if (stopVideoTaskPolling) {
                        stopVideoTaskPolling()
                      }
                      handleResend(true)
                    }}
                  >
                    <ReloadIcon className="mr-[4px] hover:text-gray-500" />
                    重新生成
                  </div>
                  {(data.generate_video?.task_status === 'SUCCEEDED' || data.generate_video?.is_valid == 1) &&
                    data.generate_video?.url &&
                    !isUrlExpired(data.generate_video.url) && (
                      <div className="flexRowStart">
                        <div className="mx-[8px] mt-[6px] h-[14px] w-[1px] bg-[#DDE3F2]"></div>
                        <div
                          className="flexRowStart chatMessageBotSize cursonCase mt-[6px]"
                          onClick={async () => {
                            if (data.generate_video?.url) {
                              try {
                                // 使用 fetch 获取视频文件，然后创建 blob 进行下载
                                const response = await customFetch(data.generate_video.url)
                                const blob = await response.blob()
                                const blobUrl = window.URL.createObjectURL(blob)
                                const link = document.createElement('a')
                                link.href = blobUrl
                                link.download = data.generate_video.name || 'video.mp4'
                                document.body.appendChild(link)
                                link.click()
                                document.body.removeChild(link)
                                // 释放 blob URL
                                window.URL.revokeObjectURL(blobUrl)
                                message.success('视频下载成功')
                              } catch (error) {
                                console.error('下载视频失败:', error)
                                message.error('下载视频失败，请稍后重试')
                              }
                            }
                          }}
                        >
                          <svg
                            className="mr-[4px] hover:text-gray-500"
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <path
                              d="M12.5 7.99805C12.776 7.99805 12.9998 8.22206 13 8.49805V10.498C13 11.8788 11.8807 12.998 10.5 12.998H3.5C2.11929 12.998 1 11.8788 1 10.498V8.49805C1.00019 8.22206 1.22397 7.99805 1.5 7.99805C1.77603 7.99805 1.99981 8.22206 2 8.49805V10.498C2 11.3265 2.67157 11.998 3.5 11.998H10.5C11.3284 11.998 12 11.3265 12 10.498V8.49805C12.0002 8.22206 12.224 7.99805 12.5 7.99805ZM7 1C7.27614 1 7.5 1.22386 7.5 1.5V8.29297L9.14648 6.64648C9.34175 6.45122 9.65825 6.45122 9.85352 6.64648C10.0488 6.84175 10.0488 7.15825 9.85352 7.35352L7.35352 9.85352C7.15825 10.0488 6.84175 10.0488 6.64648 9.85352L4.14648 7.35352C3.95122 7.15825 3.95122 6.84175 4.14648 6.64648C4.34175 6.45122 4.65825 6.45122 4.85352 6.64648L6.5 8.29297V1.5C6.5 1.22386 6.72386 1 7 1Z"
                              fill="#7587A5"
                            />
                          </svg>
                          下载视频
                        </div>
                      </div>
                    )}
                </div>
              ) : data.generate_video?.task_status === 'PENDING' || data.generate_video?.task_status === 'RUNNING' ? (
                <div
                  className="flexRowStart chatMessageBotSize cursonCase ml-[34px] mt-[6px]"
                  onClick={async () => {
                    if (data.generate_video?.task_id) {
                      try {
                        // 先停止轮询，避免取消后还会继续查询
                        const stopFn = useChatStore.getState().stopVideoTaskPolling
                        if (stopFn) {
                          stopFn()
                        }
                        // 然后更新状态为 CANCELED
                        updateVideoTaskStatus(chatId, {
                          task_status: 'CANCELED',
                          url: null,
                          name: null,
                          is_valid: 0,
                          task_id: data.generate_video.task_id
                        })
                        // 最后调用取消接口
                        await cancelVideoTaskAPI(data.generate_video.task_id)
                      } catch (error) {
                        console.error('停止视频生成失败:', error)
                      }
                    }
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    className="mr-[4px]"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_387_17616)">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M1.5 7C1.5 3.96243 3.96243 1.5 7 1.5C10.0376 1.5 12.5 3.96243 12.5 7C12.5 10.0376 10.0376 12.5 7 12.5C3.96243 12.5 1.5 10.0376 1.5 7ZM7 0.5C3.41015 0.5 0.5 3.41015 0.5 7C0.5 10.5899 3.41015 13.5 7 13.5C10.5899 13.5 13.5 10.5899 13.5 7C13.5 3.41015 10.5899 0.5 7 0.5ZM6 5C5.44772 5 5 5.44772 5 6V8C5 8.55229 5.44772 9 6 9H8C8.55229 9 9 8.55229 9 8V6C9 5.44772 8.55229 5 8 5H6Z"
                        fill="#7587A5"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_387_17616">
                        <rect width="14" height="14" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  停止生成
                </div>
              ) : null)}
          </div>
        )}
      </div>
    </div>
  )
}
